import React, { useState } from "react";
import LayerAdmin from "../LayerAdmin";
import { toast } from "react-toastify";
import ButtonLoader from "../../zamzam/components/ButtonLoader";
import { generateRoadnetRoutePlanning } from "../api/monthly";

/**
 * Parses a single text field into `employee_ids`: one id, or comma-separated ids.
 */
function parseEmployeeIds(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const RoutePlanningGenerate = () => {
  const today = new Date().toISOString().split("T")[0];
  const [employeeInput, setEmployeeInput] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);

  const handleEmployeeInputChange = (value: string) => {
    setEmployeeInput(value.replace(/,/g, ""));
  };

  const handleEmployeeInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ",") {
      e.preventDefault();
    }
  };

  const getDateFromIso = (date: string) => {
    const [year, month, day] = date.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    const startDateObj = new Date(newStartDate);
    const endDateObj = new Date(endDate);
    setStartDate(newStartDate);
    if (startDateObj > endDateObj) {
      setEndDate(newStartDate);
      return;
    }

    const maxEndDate = new Date(startDateObj);
    maxEndDate.setDate(startDateObj.getDate() + 30);
    if (endDateObj > maxEndDate) {
      setEndDate(maxEndDate.toISOString().split("T")[0]);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(newEndDate);

    if (endDateObj < startDateObj) {
      setEndDate(startDate);
      return;
    }

    setEndDate(newEndDate);
  };

  const getMaxAllowedDate = () => {
    const maxAllowedDate = new Date();
    maxAllowedDate.setDate(maxAllowedDate.getDate() + 30);
    return maxAllowedDate.toISOString().split("T")[0];
  };

  const getMaxAllowedEndDate = () => {
    const start = new Date(startDate);
    const maxAllowedEndDate = new Date(start);
    maxAllowedEndDate.setDate(start.getDate() + 30);
    return maxAllowedEndDate.toISOString().split("T")[0];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const employee_ids = parseEmployeeIds(employeeInput);
    if (employee_ids.length === 0) {
      toast.error("Enter an employee ID.");
      return;
    }
    if (employee_ids.length > 1) {
      toast.error("Only one employee ID is allowed.");
      return;
    }
    if (!startDate) {
      toast.error("Select a start date.");
      return;
    }
    if (!endDate) {
      toast.error("Select an end date.");
      return;
    }
    if (endDate < startDate) {
      toast.error("End date cannot be before start date.");
      return;
    }
    const dayDiff =
      (getDateFromIso(endDate).getTime() - getDateFromIso(startDate).getTime()) /
      (1000 * 60 * 60 * 24);
    if (dayDiff > 30) {
      toast.error("Date range cannot be more than 31 days.");
      return;
    }

    try {
      setLoading(true);
      const data = await generateRoadnetRoutePlanning({
        employee_ids,
        start_date: startDate,
        end_date: endDate,
      } as any);
      const msg =
        (data && typeof data === "object" && "message" in data && typeof (data as any).message === "string"
          ? (data as any).message
          : null) || "Request completed.";
      toast.success(msg);
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error("Please try again later.");
      } else if (error?.response?.status === 500) {
        toast.error("Something went wrong. Please try again.");
      } else {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
          detail.forEach((err: { msg?: string }) =>
            toast.error(err?.msg || "Validation error.")
          );
        } else {
          toast.error(detail?.msg || detail || error?.message || "Request failed.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayerAdmin>
      <div className="flex items-center px-5 pt-5 gap-3">
        <h3 className="text-xl font-bold text-gray-700">Route planning generate</h3>
      </div>
      <form onSubmit={handleSubmit} className="p-4 lg:w-1/3 sm:w-full">
        <div className="my-2">
          <label className="block font-semibold mb-1" htmlFor="employee_ids">
            Employee ID
          </label>
          <input
            id="employee_ids"
            type="text"
            value={employeeInput}
            onChange={(e) => handleEmployeeInputChange(e.target.value)}
            onKeyDown={handleEmployeeInputKeyDown}
            className="w-full border px-3 py-2 rounded border-gray-300"
            placeholder="Enter employee ID"
            autoComplete="off"
          />
        </div>
        <div className="my-2">
          <label className="block font-semibold mb-1" htmlFor="start_date">
            Start Date
          </label>
          <input
            id="start_date"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            onKeyDown={(e) => e.preventDefault()}
            min={today}
            max={getMaxAllowedDate()}
            className="w-full border px-3 py-2 rounded border-gray-300"
          />
        </div>
        <div className="my-2">
          <label className="block font-semibold mb-1" htmlFor="end_date">
            End Date
          </label>
          <input
            id="end_date"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            onKeyDown={(e) => e.preventDefault()}
            min={startDate}
            max={getMaxAllowedEndDate()}
            className="w-full border px-3 py-2 rounded border-gray-300"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2 mt-2"
        >
          Submit {loading && <ButtonLoader />}
        </button>
      </form>
    </LayerAdmin>
  );
};

export default RoutePlanningGenerate;
